import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../firebase/config';

export default function Login({ onSwitch }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [idleNotice, setIdleNotice] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem('idleTimeout') === '1') {
        setIdleNotice(true);
        sessionStorage.removeItem('idleTimeout');
      }
    } catch {}
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setIdleNotice(false);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch {
      setError('Invalid email or password. Please try again.');
    }
    setLoading(false);
  };

  // Reset mail goes out through SendGrid from our verified sender, since the
  // default Firebase address gets quarantined by corporate mail filters.
  const handleReset = async () => {
    if (!email) { setError('Enter your email address first.'); return; }
    setError('');
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'sendPasswordReset');
      await fn({ email: email.trim().toLowerCase() });
      setResetSent(true);
    } catch {
      try {
        await sendPasswordResetEmail(auth, email);
        setResetSent(true);
      } catch {
        setError('Could not send reset email. Check the address and try again.');
      }
    }
  };

  return (
    <div style={styles.screen}>
      <div style={styles.box}>
        <div style={styles.eyebrow}>The Corporate Fixers</div>
        <div style={styles.title}>AI Readiness<br />Portal</div>
        <div style={styles.subtitle}>From Curious to Capable</div>
        {idleNotice && (
          <div style={styles.idleNotice}>
            You were signed out due to inactivity. Please sign in again.
          </div>
        )}
        <form onSubmit={handleLogin}>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input style={styles.input} type="email" placeholder="your@company.com" value={email} onChange={e => setEmail(e.target.value)} required />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input style={styles.input} type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required />
          </div>
          {error && <div style={styles.error}>{error}</div>}
          {resetSent && <div style={styles.success}>Password reset email sent. Check your inbox.</div>}
          <button style={styles.btn} type="submit" disabled={loading}>{loading ? 'Signing in...' : 'Sign In →'}</button>
        </form>
        <div style={styles.forgotRow}><span style={styles.forgot} onClick={handleReset}>Forgot your password?</span></div>
        <div style={styles.divider}>— or —</div>
        <button style={styles.registerBtn} type="button" onClick={onSwitch}>Register with a Company Code →</button>
        <div style={styles.notice}>Have an access code from your program coordinator? Click Register above.</div>
      </div>
    </div>
  );
}

const styles = {
  screen: { background: '#1B2A4A', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  box: { background: '#FAF8F5', width: '100%', maxWidth: '400px', padding: '48px 40px' },
  eyebrow: { fontSize: '10px', fontWeight: '600', letterSpacing: '2.5px', textTransform: 'uppercase', color: '#C0392B', marginBottom: '6px', fontFamily: 'DM Sans, sans-serif' },
  title: { fontFamily: 'Playfair Display, serif', fontSize: '30px', fontWeight: '900', color: '#1B2A4A', lineHeight: '1.2', marginBottom: '4px' },
  subtitle: { fontSize: '13px', color: '#8A8A8A', fontWeight: '300', marginBottom: '32px', fontFamily: 'DM Sans, sans-serif', fontStyle: 'italic' },
  idleNotice: { background: '#1B2A4A', color: '#FFFFFF', padding: '12px 16px', fontSize: '13px', marginBottom: '20px', fontFamily: 'DM Sans, sans-serif', borderLeft: '4px solid #D4A847', lineHeight: '1.5' },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '11px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', color: '#8A8A8A', marginBottom: '6px', fontFamily: 'DM Sans, sans-serif' },
  input: { width: '100%', border: '1.5px solid #E8E6E2', padding: '11px 14px', fontFamily: 'DM Sans, sans-serif', fontSize: '14px', background: '#FFFFFF', color: '#3D3D3D', outline: 'none' },
  error: { background: '#fdecea', color: '#a32d2d', padding: '10px 14px', fontSize: '13px', marginBottom: '12px', fontFamily: 'DM Sans, sans-serif' },
  success: { background: '#eaf3de', color: '#3b6d11', padding: '10px 14px', fontSize: '13px', marginBottom: '12px', fontFamily: 'DM Sans, sans-serif' },
  btn: { width: '100%', background: '#1B2A4A', color: '#FFFFFF', border: 'none', padding: '14px', fontFamily: 'Playfair Display, serif', fontSize: '15px', fontWeight: '700', cursor: 'pointer', marginTop: '8px' },
  forgotRow: { textAlign: 'center', marginTop: '16px' },
  forgot: { fontSize: '12px', color: '#C0392B', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'DM Sans, sans-serif' },
  divider: { textAlign: 'center', fontSize: '12px', color: '#8A8A8A', margin: '20px 0', fontFamily: 'DM Sans, sans-serif' },
  registerBtn: { width: '100%', background: 'transparent', color: '#1B2A4A', border: '1.5px solid #1B2A4A', padding: '12px', fontFamily: 'DM Sans, sans-serif', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  notice: { marginTop: '16px', fontSize: '11px', color: '#8A8A8A', lineHeight: '1.6', textAlign: 'center', fontFamily: 'DM Sans, sans-serif', borderTop: '1px solid #E8E6E2', paddingTop: '16px' },
};
